import {
  Alert,
  Button,
  Collapse,
  Flex,
  FormLabel,
  Icon,
  Link,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import download from 'downloadjs';
import { BsPaperclip } from 'react-icons/all';
import { Heading } from '~/components';
import { SinistersDocuments, SinistersView } from '~/types/sinisters';
import { getSinisterPdf } from '~/services/Sinisters';

export const GeneralInformation: React.FC<{
  sinister: SinistersView;
  isAdmin?: boolean;
}> = ({ sinister, isAdmin }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isFetching, setFetching] = useState(false);
  const [show, setShow] = React.useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const handleToggle = () => setShow(!show);

  function handleDownload(tipo: number) {
    setIsDownloading(true);
    const url = SinistersDocuments[tipo];
    getSinisterPdf(sinister.id.toString(), url)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            `${url}_${sinister?.numero_sinistro}`,
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(err => {
        toast({
          title: 'Documento não encontrado',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }

  return (
    <>
      <Heading> Informações gerais</Heading>
      <Collapse startingHeight={100} endingHeight="fit-content" in={show}>
        <Stack gridGap={4}>
          <Flex justify="space-between">
            <Flex width="150px">
              <Text size="150px">
                <FormLabel>N° do sinistro:</FormLabel>
                <Text size="sm">{sinister.numero_sinistro}</Text>
              </Text>
            </Flex>
            <Flex width="150px">
              <Text size="150px">
                <FormLabel>Cliente:</FormLabel>
                <Text size="sm">{sinister.empresa.cliente}</Text>
                <Text size="sm" fontSize="14px">
                  {sinister.empresa.cnpj}
                </Text>
              </Text>
            </Flex>
            <Flex width="150px">
              <Text size="150px">
                <FormLabel>Grupo:</FormLabel>
                <Text size="sm">{sinister.empresa.grupo}</Text>
              </Text>
            </Flex>
          </Flex>
          <Flex justify="space-between">
            <Flex width="150px">
              <Text size="150px">
                <FormLabel>Série:</FormLabel>
                <Text size="sm">{sinister.numero_serie}</Text>
              </Text>
            </Flex>
            <Flex width="150px">
              <Text size="150px">
                <FormLabel>SKU:</FormLabel>
                <Text size="sm">{sinister.equipamento.sku}</Text>
              </Text>
            </Flex>
            <Flex width="150px">
              <Text size="150px">
                <FormLabel>Equipamento:</FormLabel>
                <Text size="sm">{sinister.equipamento.tipo_equipamento}</Text>
                <Text size="sm" fontSize="14px">
                  {sinister.equipamento.modelo}
                </Text>
              </Text>
            </Flex>
          </Flex>
          <Flex justify="space-between">
            <Flex width="150px">
              <Text size="150px">
                <FormLabel>Responsável:</FormLabel>
                <Text size="sm">{sinister.comunicado_por}</Text>
              </Text>
            </Flex>
            <Flex width="150px">
              <Text size="150px">
                <FormLabel>Data:</FormLabel>
                <Text size="sm">{sinister.formattedDate}</Text>
              </Text>
            </Flex>
            <Flex width="150px">
              <Text size="150px !important">
                <FormLabel>Tipo:</FormLabel>
                <Text size="sm">{sinister.tipo.nome}</Text>
              </Text>
            </Flex>
          </Flex>
          <Flex justify="space-between">
            <Flex width="150px">
              <Text size="150px">
                <FormLabel>Boletim de ocorrência (BO):</FormLabel>
                {sinister.boletim_ocorrencia ? (
                  <Text
                    fontSize="14px"
                    target="_blank"
                    color="gray.800"
                    _hover={{
                      color: 'gray.900',
                      textDecoration: 'underline',
                    }}
                    cursor="pointer"
                    onClick={() => handleDownload(2)}
                  >
                    Download
                    <Icon as={BsPaperclip} fontSize="xl" />
                  </Text>
                ) : (
                  <Text>-</Text>
                )}
              </Text>
            </Flex>
            <Flex width="150px">
              <Text size="150px">
                <FormLabel>Declaração:</FormLabel>
                {sinister.declaracao ? (
                  <Text
                    fontSize="14px"
                    target="_blank"
                    color="gray.800"
                    _hover={{
                      color: 'gray.900',
                      textDecoration: 'underline',
                    }}
                    cursor="pointer"
                    onClick={() => handleDownload(0)}
                  >
                    Download
                    <Icon as={BsPaperclip} fontSize="xl" />
                  </Text>
                ) : (
                  <Text>-</Text>
                )}
              </Text>
            </Flex>
            <Flex width="150px">
              <Text size="150px !important">
                <FormLabel>Outros:</FormLabel>
                {sinister.outro ? (
                  <Text
                    fontSize="14px"
                    target="_blank"
                    color="gray.800"
                    _hover={{
                      color: 'gray.900',
                      textDecoration: 'underline',
                    }}
                    cursor="pointer"
                    onClick={() => handleDownload(3)}
                  >
                    Download
                    <Icon as={BsPaperclip} fontSize="xl" />
                  </Text>
                ) : (
                  <Text>-</Text>
                )}
              </Text>
            </Flex>
          </Flex>
          {isAdmin && (
            <Flex justify="space-between">
              <Flex width="150px">
                <Text size="150px">
                  <FormLabel>Seguradora:</FormLabel>
                  <Text size="sm">{sinister.seguradora?.nome || '-'}</Text>
                </Text>
              </Flex>
              <Flex width="150px">
                <Text size="150px">
                  <FormLabel>N° apólice:</FormLabel>
                  <Text size="sm">{sinister.numero_apolice || '-'}</Text>
                </Text>
              </Flex>
              <Flex width="150px">
                <Text size="150px !important">
                  <FormLabel>Carta Positivo:</FormLabel>
                  {sinister.existe_carta_positivo ? (
                    <Text
                      fontSize="14px"
                      target="_blank"
                      color="gray.800"
                      _hover={{
                        color: 'gray.900',
                        textDecoration: 'underline',
                      }}
                      cursor="pointer"
                      onClick={() => handleDownload(4)}
                    >
                      Download
                      <Icon as={BsPaperclip} fontSize="xl" />
                    </Text>
                  ) : (
                    <Text>-</Text>
                  )}
                </Text>
              </Flex>
            </Flex>
          )}
          {isAdmin && (
            <Flex justify="space-between">
              <Flex width="150px">
                <Text size="150px">
                  <FormLabel>Nota de remessa:</FormLabel>
                  {sinister.existe_nota_de_remessa ? (
                    <Text
                      fontSize="14px"
                      target="_blank"
                      color="gray.800"
                      _hover={{
                        color: 'gray.900',
                        textDecoration: 'underline',
                      }}
                      cursor="pointer"
                      onClick={() => handleDownload(5)}
                    >
                      Download
                      <Icon as={BsPaperclip} fontSize="xl" />
                    </Text>
                  ) : (
                    <Text>-</Text>
                  )}
                </Text>
              </Flex>
              <Flex width="150px">
                <Text size="150px">
                  <FormLabel>Orçamento de reparo:</FormLabel>
                  {sinister.existe_orcamento_de_reparo ? (
                    <Text
                      fontSize="14px"
                      target="_blank"
                      color="gray.800"
                      _hover={{
                        color: 'gray.900',
                        textDecoration: 'underline',
                      }}
                      cursor="pointer"
                      onClick={() => handleDownload(6)}
                    >
                      Download
                      <Icon as={BsPaperclip} fontSize="xl" />
                    </Text>
                  ) : (
                    <Text>-</Text>
                  )}
                </Text>
              </Flex>
              <Flex width="150px">
                <Text size="150px !important">
                  <FormLabel>Nota de débito:</FormLabel>
                  {sinister.existe_nota_de_debito ? (
                    <Text
                      fontSize="14px"
                      target="_blank"
                      color="gray.800"
                      _hover={{
                        color: 'gray.900',
                        textDecoration: 'underline',
                      }}
                      cursor="pointer"
                      onClick={() => handleDownload(8)}
                    >
                      Download
                      <Icon as={BsPaperclip} fontSize="xl" />
                    </Text>
                  ) : (
                    <Text>-</Text>
                  )}
                </Text>
              </Flex>
            </Flex>
          )}
          {sinister.existe_comprovante && isAdmin && (
            <Flex justify="space-between">
              <Flex width="150px">
                <Alert
                  bg="rgba(255, 215, 71, 0.4);"
                  color="#88460D"
                  display="block"
                  fontSize="16px"
                  borderRadius={5}
                >
                  <FormLabel>Comprovante:</FormLabel>
                  <Text
                    fontSize="14px"
                    target="_blank"
                    color="#88460D"
                    _hover={{
                      color: '#88460D',
                      textDecoration: 'underline',
                    }}
                    cursor="pointer"
                    onClick={() => handleDownload(7)}
                  >
                    Download
                    <Icon as={BsPaperclip} fontSize="xl" />
                  </Text>
                </Alert>
              </Flex>
            </Flex>
          )}
        </Stack>
      </Collapse>
      <Button
        size="sm"
        onClick={handleToggle}
        mt="1rem"
        alignSelf="flex-end"
        width="120px"
        style={{ marginRight: '30px' }}
        fontSize={14}
      >
        {show ? 'Diminuir' : 'Aumentar'}
        {!show ? (
          <ChevronDownIcon fontSize={14} ml="2" />
        ) : (
          <ChevronUpIcon fontSize={14} ml="2" />
        )}
      </Button>
    </>
  );
};
